import { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Heart, Repeat2, Flag, ListPlus, ListEnd } from "lucide-react";
import { usePlaylistDialog } from "@/components/Collections";
import { toast } from "sonner";
import { usePlayer, audio } from "@/store/player";
import { Waveform } from "@/components/Waveform";
import { ArtistBadge } from "@/components/ArtistBadge";
import { DmcaDialog } from "@/components/DmcaDialog";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { fmtNum, hubByKey } from "@/lib/hubs";

export const useIsCurrent = (id) => usePlayer((s) => s.queue[s.index]?.track_id === id);

export const PlayButton = ({ track, list, size = 40, testId }) => {
  const isCur = useIsCurrent(track.track_id);
  const playing = usePlayer((s) => s.isPlaying) && isCur;
  const playTrack = usePlayer((s) => s.playTrack);
  return (
    <button data-testid={testId || `play-btn-${track.track_id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); playTrack(track, list); }}
      className="rounded-full flex items-center justify-center bg-white text-black hover:scale-105 active:scale-95 transition-transform shadow-lg shrink-0"
      style={{ width: size, height: size }} aria-label={playing ? "Pause" : "Play"}>
      {playing ? <Pause size={size * 0.42} fill="black" /> : <Play size={size * 0.42} fill="black" className="ml-0.5" />}
    </button>
  );
};

export const SocialToggle = ({ track, kind, initial = false, initialCount }) => {
  const [active, setActive] = useState(initial);
  const [count, setCount] = useState(initialCount ?? track[kind === "like" ? "likes" : "reposts"]);
  const Icon = kind === "like" ? Heart : Repeat2;
  const go = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/tracks/${track.track_id}/${kind}`);
      setActive(data.active);
      setCount(data.count);
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Sign in to interact" : errMsg(err));
    }
  };
  return (
    <button data-testid={`${kind}-btn-${track.track_id}`} onClick={go}
      className={`flex items-center gap-1 text-xs font-mono transition-colors ${active ? "text-[var(--hub)]" : "text-zinc-400 hover:text-white"}`}>
      <Icon size={15} fill={active && kind === "like" ? "currentColor" : "none"} /> {fmtNum(count)}
    </button>
  );
};

export const TrackRow = ({ track, list, rank, showWave = true }) => {
  const isCur = useIsCurrent(track.track_id);
  const t = usePlayer((s) => (isCur ? s.currentTime : 0));
  const dur = usePlayer((s) => (isCur ? s.duration : track.duration));
  const addToQueue = usePlayer((s) => s.addToQueue);
  const [dmca, setDmca] = useState(false);
  const hub = hubByKey(track.genres?.[0]);
  return (
    <div data-testid={`track-row-${track.track_id}`} className={`group flex items-center gap-3 sm:gap-4 p-2 sm:p-3 rounded-xl transition-colors ${isCur ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"}`}>
      {rank != null && <span className="w-6 text-right font-mono text-sm text-zinc-500">{rank}</span>}
      <div className="relative w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-zinc-800">
        {track.cover_url && <img src={mediaUrl(track.cover_url)} alt="" className="w-full h-full object-cover" loading="lazy" />}
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
          <PlayButton track={track} list={list} size={34} />
        </div>
      </div>
      <div className="min-w-0 flex-1 md:flex-none md:w-56 md:shrink-0">
        <Link to={`/track/${track.track_id}`} data-testid={`track-link-${track.track_id}`} className="block font-semibold truncate hover:underline">{track.title}</Link>
        <Link to={`/artist/${track.artist?.slug}`} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white truncate">
          <span className="truncate">{track.artist?.name}</span> <ArtistBadge badge={track.artist?.badge} size={13} />
        </Link>
        <div className="flex gap-1.5 mt-1">
          {track.genres?.map((g) => <span key={g} className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color: hubByKey(g)?.color, background: "rgba(255,255,255,0.05)" }}>{hubByKey(g)?.name.split(" /")[0]}</span>)}
        </div>
      </div>
      {showWave && (
        <div className="hidden md:block flex-1 min-w-0">
          <Waveform peaks={track.peaks} bars={90} height={36} duration={dur} progress={dur ? t / dur : 0} color={hub?.color || "var(--hub)"}
            testId={`row-waveform-${track.track_id}`} onSeek={(f) => (isCur ? usePlayer.getState().seek(f * (audio.duration || dur)) : usePlayer.getState().playTrack(track, list))} />
        </div>
      )}
      <div className="flex items-center gap-3 sm:gap-4 ml-auto shrink-0">
        <span className="hidden sm:inline text-xs font-mono text-zinc-500" data-testid={`track-streams-${track.track_id}`}>{fmtNum(track.chart_streams ?? track.streams)} plays</span>
        <SocialToggle track={track} kind="like" />
        <span className="hidden lg:block"><SocialToggle track={track} kind="repost" /></span>
        <button data-testid={`queue-add-${track.track_id}`} onClick={() => { addToQueue(track); toast.success("Added to queue"); }} className="text-zinc-500 hover:text-white hidden sm:block" aria-label="Add to queue" title="Add to queue"><ListEnd size={16} /></button>
        <button data-testid={`playlist-add-${track.track_id}`} onClick={() => usePlaylistDialog.getState().open(track)} className="text-zinc-500 hover:text-white" aria-label="Add to playlist" title="Add to playlist"><ListPlus size={16} /></button>
        <button data-testid={`dmca-btn-${track.track_id}`} onClick={() => setDmca(true)} className="text-zinc-600 hover:text-red-400" aria-label="Report copyright"><Flag size={14} /></button>
      </div>
      <DmcaDialog open={dmca} onOpenChange={setDmca} track={track} />
    </div>
  );
};

export const TrackCard = ({ track, list }) => (
  <Link to={`/track/${track.track_id}`} data-testid={`track-card-${track.track_id}`} className="group block reveal">
    <div className="relative aspect-square rounded-2xl overflow-hidden bg-zinc-800 border border-white/[0.06]">
      {track.cover_url && <img src={mediaUrl(track.cover_url)} alt={track.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" loading="lazy" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      <div className="absolute bottom-3 right-3 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300">
        <PlayButton track={track} list={list} size={44} />
      </div>
    </div>
    <div className="mt-3 font-semibold truncate">{track.title}</div>
    <div className="flex items-center gap-1 text-sm text-zinc-400 truncate">{track.artist?.name} <ArtistBadge badge={track.artist?.badge} size={13} /></div>
  </Link>
);
